/**
 * FilterPresetsBar — Iter · Feb 2026
 *
 * Companion to `PlanningBoard.jsx`'s `FiltersBar`. Renders:
 *   • A list of saved-preset chips (personal + workspace-wide) at the
 *     top of the filter row so PMs can jump to a favourite combo in
 *     one tap.
 *   • A "Save preset" action that appears alongside the amber Apply
 *     bar when there are pending filter changes.
 *   • Inline rename / delete via a chip menu.
 *
 * A "preset" captures the FULL Planning-Board filter surface:
 *   { filters, swimBy, quickDueFilter, onlyWatched, quickReport? }
 * — everything the user asked to persist per follow-up.
 *
 * Backend: /api/v1/workspaces/{ws}/planning/filter-presets (GET/POST)
 *          /api/v1/planning/filter-presets/{id}          (PATCH/DELETE)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bookmark, Users, User as UserIcon, Trash2, MoreHorizontal, Save, Link as LinkIcon } from "lucide-react";

/** Small chip button for the horizontal preset row. */
const PresetChip = ({ preset, active, hotkey, onApply, onDelete, onRename, onCopyLink }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(preset.name);
  const isTeam = preset.scope === "workspace";
  return (
    <div className="relative inline-flex items-stretch shrink-0" data-testid={`planning-preset-chip-${preset.id}`}>
      {editing ? (
        <form
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) onRename(name.trim()); setEditing(false); }}
          className="inline-flex items-stretch"
        >
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => { if (name.trim()) onRename(name.trim()); setEditing(false); }}
            className="h-7 px-2 border border-primary text-xs w-32 bg-white rounded-none"
            data-testid={`planning-preset-rename-input-${preset.id}`}
          />
        </form>
      ) : (
        <button
          type="button"
          onClick={onApply}
          className={`text-xs px-2 h-7 border rounded-none inline-flex items-center gap-1 ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-muted/40 hover:bg-muted text-foreground"}`}
          title={`${isTeam ? "Team preset · shared with the workspace" : "Personal preset"}${hotkey ? ` · press ${hotkey} to apply` : ""}`}
          data-testid={`planning-preset-apply-${preset.id}`}
        >
          {isTeam
            ? <Users className="w-3 h-3" aria-hidden="true" />
            : <UserIcon className="w-3 h-3" aria-hidden="true" />}
          <span className="truncate max-w-[8rem]">{preset.name}</span>
          {hotkey && (
            <span
              className={`ml-0.5 text-[9px] leading-none font-mono px-1 py-0.5 border ${active ? "border-primary-foreground/50 bg-primary-foreground/20" : "border-border bg-background/60 text-muted-foreground"}`}
              title={`Keyboard shortcut: ${hotkey}`}
              data-testid={`planning-preset-hotkey-${preset.id}`}
            >{hotkey}</span>
          )}
        </button>
      )}
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="text-xs px-1 h-7 border-y border-r rounded-none hover:bg-muted"
            aria-label="Preset actions"
            data-testid={`planning-preset-menu-${preset.id}`}
          >
            <MoreHorizontal className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-1 rounded-none" align="end">
          <button
            type="button"
            onClick={() => { setMenuOpen(false); setEditing(true); }}
            className="w-full text-left text-xs px-2 py-1.5 hover:bg-muted"
            data-testid={`planning-preset-rename-${preset.id}`}
          >Rename</button>
          <button
            type="button"
            onClick={() => { setMenuOpen(false); onCopyLink && onCopyLink(); }}
            className="w-full text-left text-xs px-2 py-1.5 hover:bg-muted inline-flex items-center gap-1"
            data-testid={`planning-preset-copy-link-${preset.id}`}
          ><LinkIcon className="w-3 h-3" /> Copy share link</button>
          <button
            type="button"
            onClick={() => { setMenuOpen(false); if (window.confirm(`Delete preset "${preset.name}"?`)) onDelete(); }}
            className="w-full text-left text-xs px-2 py-1.5 hover:bg-rose-50 text-rose-600 inline-flex items-center gap-1"
            data-testid={`planning-preset-delete-${preset.id}`}
          ><Trash2 className="w-3 h-3" /> Delete</button>
        </PopoverContent>
      </Popover>
    </div>
  );
};


export default function FilterPresetsBar({
  workspaceId,
  // Callback used to snapshot the current planning-board state when
  // saving. Parent supplies a function so we don't have to know every
  // state variable name here.
  getCurrentState,
  // Callback fired when a preset is applied — parent restores its
  // internal state accordingly (mirrors BoardTemplatesButton).
  applyState,
  // Anchor element that lives inside the amber Apply-bar; only the
  // "Save current as preset" action renders here so pending changes
  // get an obvious "capture-me" action.
  slot = "list",
  // The currently-committed filter fingerprint so we can highlight
  // the matching preset chip (optional).
  activeFingerprint = "",
}) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingOpen, setSavingOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState("personal");

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}/planning/filter-presets`);
      setPresets(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-critical: presets fall back to empty list.
      if (e?.response?.status && e.response.status !== 404) {
        toast.error(apiErrorMessage(e, "Could not load presets"));
      }
    } finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // Iter · Feb 2026 — Two <FilterPresetsBar> instances are mounted on
  // the same page (save-slot inside the amber Apply bar, list-slot at
  // the top of the filter row). They keep independent `presets` state
  // fetched at mount, so a save in one instance never reaches the
  // other. A tiny window event lets both instances refetch when the
  // authoritative row changes anywhere.
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener("planning-presets-changed", handler);
    return () => window.removeEventListener("planning-presets-changed", handler);
  }, [load]);

  const notify = () => {
    try { window.dispatchEvent(new CustomEvent("planning-presets-changed")); }
    catch { /* Safari <10 fallback */ }
  };

  const sortedPresets = useMemo(
    () => [...presets].sort((a, b) => {
      // Personal first, then workspace; A→Z inside each.
      if (a.scope !== b.scope) return a.scope === "personal" ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    }),
    [presets],
  );

  const savePreset = async () => {
    const name = (newName || "").trim();
    if (!name) { toast.error("Give this preset a name"); return; }
    const state = getCurrentState ? getCurrentState() : {};
    try {
      const { data } = await api.post(
        `/workspaces/${workspaceId}/planning/filter-presets`,
        { name, scope: newScope, state },
      );
      // Merge / upsert by id.
      setPresets((prev) => {
        const idx = prev.findIndex((p) => p.id === data.id);
        if (idx >= 0) { const copy = [...prev]; copy[idx] = data; return copy; }
        return [...prev, data];
      });
      toast.success(`Preset "${name}" saved`);
      setSavingOpen(false);
      setNewName("");
      setNewScope("personal");
      notify();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save preset"));
    }
  };

  const applyPreset = useCallback((preset) => {
    if (!preset || !preset.state) return;
    applyState && applyState(preset.state);
    toast.success(`Applied "${preset.name}"`);
  }, [applyState]);

  // Iter · Feb 2026 — Keyboard shortcuts 1-9 apply the first nine
  // chips in the sorted list. Ignored when the user is typing in an
  // input, textarea, contenteditable, or holding a modifier so plain
  // typing / copy-paste is never hijacked. Only the LIST-slot mounts
  // the listener so we don't fire twice.
  useEffect(() => {
    if (slot !== "list") return;
    const handler = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tgt = e.target;
      const tag = ((tgt && tgt.tagName) || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (tgt && (tgt.isContentEditable || tgt.closest?.("[contenteditable='true']"))) return;
      const n = parseInt(e.key, 10);
      if (!Number.isFinite(n) || n < 1 || n > 9) return;
      const target = sortedPresets[n - 1];
      if (target) {
        e.preventDefault();
        applyPreset(target);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [slot, sortedPresets, applyPreset]);

  // Iter · Feb 2026 — Share via URL. Copy `?preset=<id>` onto the
  // current planning-board URL and drop the fingerprint into the
  // clipboard so a PM can Slack "check this filter view" to a
  // colleague. On mount, if the URL already carries `?preset=<id>`,
  // apply that preset once and strip the query so refreshes don't
  // clobber later edits.
  const copyShareLink = async (preset) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("preset", preset.id);
      // Preserve only the /app/planning path — dropping other query
      // params keeps the link clean.
      const share = `${url.origin}${url.pathname}?preset=${preset.id}`;
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(share);
      } else {
        // Fallback for insecure contexts / older Safari.
        const ta = document.createElement("textarea");
        ta.value = share; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Share link copied to clipboard");
    } catch {
      toast.error("Could not copy link — please copy manually from the address bar");
    }
  };

  // Auto-apply from `?preset=<id>` query param on first load. Only
  // the list-slot handles this to avoid double-apply. Once applied,
  // we strip the query so a page reload doesn't override subsequent
  // edits.
  useEffect(() => {
    if (slot !== "list" || presets.length === 0) return;
    try {
      const url = new URL(window.location.href);
      const pid = url.searchParams.get("preset");
      if (!pid) return;
      const preset = presets.find((p) => p.id === pid);
      if (!preset) return;
      applyPreset(preset);
      url.searchParams.delete("preset");
      window.history.replaceState({}, "", url.toString());
    } catch { /* URL API unsupported → ignore */ }
    // Intentionally run once when presets first arrive; further URL
    // changes are user-initiated.
  }, [slot, presets.length]);

  const deletePreset = async (preset) => {
    try {
      await api.delete(`/planning/filter-presets/${preset.id}`);
      setPresets((prev) => prev.filter((p) => p.id !== preset.id));
      toast.success(`Deleted "${preset.name}"`);
      notify();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not delete preset"));
    }
  };

  const renamePreset = async (preset, newName) => {
    try {
      const { data } = await api.patch(`/planning/filter-presets/${preset.id}`, { name: newName });
      setPresets((prev) => prev.map((p) => (p.id === preset.id ? data : p)));
      notify();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not rename preset"));
    }
  };

  // "save" slot — small chip that opens the save popover, meant to
  // sit inside the amber Apply bar so pending changes get captured.
  if (slot === "save") {
    return (
      <Popover open={savingOpen} onOpenChange={setSavingOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="text-xs px-2 h-7 border border-amber-600 text-amber-800 hover:bg-amber-100 rounded-none inline-flex items-center gap-1"
            data-testid="planning-preset-save-open"
            title="Save the currently-staged filters as a reusable preset"
          >
            <Save className="w-3 h-3" />
            Save as preset
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3 rounded-none space-y-2" align="end">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Save current filters as preset
          </div>
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); savePreset(); } }}
            placeholder="e.g. Focus · overdue tasks"
            className="h-8 w-full px-2 border border-border text-xs rounded-none"
            data-testid="planning-preset-name-input"
          />
          <div className="flex items-center gap-2 text-xs">
            <label className="inline-flex items-center gap-1 cursor-pointer" title="Only visible to you">
              <input type="radio" name="preset-scope" value="personal"
                     checked={newScope === "personal"}
                     onChange={() => setNewScope("personal")}
                     data-testid="planning-preset-scope-personal" />
              <UserIcon className="w-3 h-3" /> Personal
            </label>
            <label className="inline-flex items-center gap-1 cursor-pointer" title="Shared with the workspace">
              <input type="radio" name="preset-scope" value="workspace"
                     checked={newScope === "workspace"}
                     onChange={() => setNewScope("workspace")}
                     data-testid="planning-preset-scope-workspace" />
              <Users className="w-3 h-3" /> Team
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setSavingOpen(false)}
              className="text-xs px-2 h-7 border border-border rounded-none hover:bg-muted"
            >Cancel</button>
            <button
              type="button"
              onClick={savePreset}
              disabled={!newName.trim()}
              className="text-xs px-3 h-7 bg-primary text-primary-foreground rounded-none disabled:opacity-50"
              data-testid="planning-preset-save-confirm"
            >Save preset</button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // "list" slot — the chip strip. Shows nothing (returns null) when no
  // presets exist so we don't add empty visual noise; users first save
  // a preset via the amber Apply-bar action.
  if (loading || sortedPresets.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="planning-preset-chips">
      <Bookmark className="w-3 h-3 text-muted-foreground shrink-0" aria-hidden="true" />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
        Presets
      </span>
      {sortedPresets.map((p, idx) => (
        <PresetChip
          key={p.id}
          preset={p}
          active={activeFingerprint && activeFingerprint === presetFingerprint(p.state)}
          hotkey={idx < 9 ? String(idx + 1) : null}
          onApply={() => applyPreset(p)}
          onDelete={() => deletePreset(p)}
          onRename={(newName) => renamePreset(p, newName)}
          onCopyLink={() => copyShareLink(p)}
        />
      ))}
    </div>
  );
}

/**
 * Stable string fingerprint of a preset's state — used to highlight
 * the currently-active preset chip. Kept simple: sort arrays before
 * stringifying so `{statuses: ["done", "todo"]}` matches even when the
 * user selected them in a different order.
 */
export function presetFingerprint(state) {
  if (!state || typeof state !== "object") return "";
  const norm = (v) => {
    if (Array.isArray(v)) return [...v].sort().join(",");
    if (v && typeof v === "object") return Object.keys(v).sort().map((k) => `${k}=${norm(v[k])}`).join("|");
    return String(v ?? "");
  };
  return norm(state);
}
